import { useState } from "react";
import {
  useTheme,
  Container,
  Box,
  TextField,
  Link as MuiLink,
} from "@mui/material";
import { withPather } from "react-pather";
import { useNavigate, Navigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../Loader";
import {
  PasswordField,
  PrimaryButton,
  SendNewLinkMessage,
  Page,
} from "../general";
import { useToken } from "../../hooks";
import { setUser } from "../../slices/userSlice";
import { setToken as setUUT } from "../../slices/tokenSlice";
import { setUserLoading } from "../../slices/userLoadingSlice";
import { setTheme } from "../../slices/themeSlice";

const LinkText = ({ text, to }) => {
  const theme = useTheme();
  return (
    <MuiLink
      component={RouterLink}
      to={to}
      underline="always"
      sx={{
        color: theme.palette.text.main,
        textDecorationColor: theme.palette.text.main,
        "&:hover": {
            opacity: 0.75,
        },
      }}
    >
      {text}
    </MuiLink>
  );
};

const Login = ({ pather }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { setToken } = useToken();
  const currentUser = useSelector((state) => state.user.value);
  const isUserLoading = useSelector((state) => state.userLoading.value);
  const uut = useSelector((state) => state.token.value);

  const [isActivated, setIsActivated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activationLoading, setActivationLoading] = useState(false);
  const [authData, setAuthData] = useState({
    login: "",
    password: "",
  });

  const setAuthDataSafe = (updStateObj) => {
    setAuthData((prevState) => ({
      ...prevState,
      ...updStateObj,
    }));
  };

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  //

  const isInputValid = () => {
    if (!!!authData.login.trim()) {
      enqueueSnackbar("Login is not provided", { variant: "error" });
      return false;
    } else if (!!!authData.password.trim()) {
      enqueueSnackbar("Password is not provided", { variant: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!isInputValid()) return;
    setLoading(true);
    dispatch(setUserLoading(true));
    axios
      .post(pather.back.Auth.login, authData)
      .then((response) => {
        const data = response.data;
        dispatch(
          setUser({
            isLogined: true,
            isConfirmed: data.is_confirmed,
            email: data.email,
            username: data.username,
            uut: data.uut,
            roles: data.roles,
            primaryColor: data.primary_color,
          })
        );
        setToken(data.access_token);
        if (uut !== data.uut) dispatch(setUUT(data.uut));
        if (data?.user_theme) dispatch(setTheme(data.user_theme));
        navigate(pather.front.dashboard);
      })
      .catch(({ response: { data } }) => {
        enqueueSnackbar(data.status, { variant: "error" });
        console.log(data.status);
        if (data?.is_activated === false) setIsActivated(false);
      })
      .finally(() => {
        dispatch(setUserLoading(false));
        setLoading(false);
      });
  };

  const sendActivationLink = () => {
    setActivationLoading(true);
    axios
      .post(pather.back.confirmationLink, {
        login: authData.login,
      })
      .then((resp) => {
        const data = resp?.data;
        enqueueSnackbar(data?.status, { variant: "success" });
        setIsActivated(null);
      })
      .catch((resp) => {
        const data = resp?.response?.data;
        enqueueSnackbar(data?.status, { variant: "error" });
        console.log(data);
      })
      .finally(() => {
        setActivationLoading(false);
      });
  };

  if (isUserLoading) return <Loader boxMinHeight="550px" />;
  else if (currentUser?.isLogined)
    return <Navigate to={pather.front.dashboard} />;

  return (
    <>
      {isActivated === false && (
        <Container sx={{ pt: 2 }}>
          <SendNewLinkMessage
            loading={activationLoading}
            sendNewLink={sendActivationLink}
            message="Activate your account clicking on a link in email"
          />
        </Container>
      )}
      <Page
        label="Login"
        loading={loading}
        sx={{ my: 0, alignItems: "center" }}
      >
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
              width: { xs: "100%", sm: "70%", md: "30vw" },
              maxWidth: "500px",
            }}
          >
            <TextField
              label="Email or username"
              value={authData.login}
              onChange={(e) =>
                setAuthDataSafe({ login: e.target.value.trim() })
              }
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <PasswordField
                label="Password"
                value={authData.password}
                handleChange={(e) =>
                  setAuthDataSafe({ password: e.target.value })
                }
                show={showPassword}
                handleShow={handleClickShowPassword}
              />
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <LinkText
                  text="Forgot password?"
                  to={pather.front.resetPassword}
                />
              </Box>
            </Box>
            <PrimaryButton onClick={handleSubmit} />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              Don't have an account? &nbsp;{" "}
              <LinkText
                text="Sign up"
                to={pather.front.signup}
              />
            </Box>
          </Box>
        </Box>
      </Page>
    </>
  );
};

export default withPather()(Login);